import { Component, OnInit, OnDestroy, Input, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PartnersService } from '../../services/partners.service';
import { UsersService } from '../../services/users.service';
import { OffersService } from '../../services/offers.service';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { Partner } from '../../interfaces/partners.interface';
import { Offer } from '../../interfaces/offers.interface';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject, takeUntil } from 'rxjs';
import { MapPointer } from '../../shared/map-view/map-view.component';

@Component({
  selector: 'app-parnter-edit',
  standalone: false,
  templateUrl: './parnter-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./parnter-edit.component.scss']
})
export class ParnterEditComponent implements OnInit, OnDestroy {
  @Input() partnerId?: number;

  mapPointer: MapPointer | null = null;

  partnerForm!: FormGroup;
  loading = false;
  saving = false;
  error: string | null = null;

  // Branches data
  branches: Partner[] = [];
  branchesLoading = false;

  // Offers data
  offers: Offer[] = [];
  offersLoading = false;
  editingOfferId?: number;
  showOfferEdit = false;

  // Track if we're editing a branch vs main partner
  mainPartnerId?: number;
  currentPartner?: Partner;
  isEditingBranch = false;

  // Logo upload state
  selectedLogoFile?: File;
  localLogoPreview?: string;
  fileJustUploaded = false;

  mapCenter = { lat: 51.935619, lng: 15.506186 }; // Zielona Góra center
  mapZoom = 12;
  addressQuery: string = 'Polska Zielona Góra 65-001';

  imagesUrl = environment.ImagesUrl;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private partnersService: PartnersService,
    private usersService: UsersService,
    private offersService: OffersService,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.initForm();
  }

  initForm(): void {
    this.partnerForm = this.fb.group({
      latitude: [''],
      longitude: [''],
      info: [''],
      contact: [''],
      url: [''],
      name: [''],
      intro: [''],
      file: ['']
    });

    // Debug: Track file control changes
    this.partnerForm.get('file')?.valueChanges.subscribe(value => {
    });
  }

  ngOnInit(): void {
    // Get user's partner_id and load partner data
    if (!this.partnerId || this.partnerId <= 0) {
      
      const user = this.usersService.getCurrentUser();
      if (user && user.partner_id && user.partner_id > 0) {
        this.partnerId = user.partner_id;
       
      }
    }

    // Store the main partner ID for navigation
    this.mainPartnerId = this.partnerId;

    this.loadPartner();


    
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPartner(): void {
    if (!this.partnerId) return;
    
    this.loading = true;
    this.partnersService.getPartner(this.partnerId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (partner) => {
        this.currentPartner = partner;
        this.isEditingBranch = partner.parent_id ? partner.parent_id > 0 : false;

        const patchData: any = {
          latitude: partner.latitude || '',
          longitude: partner.longitude || '',
          info: partner.info || '',
          intro: partner.intro || '',
          contact: partner.contact || '',
          url: partner.url || '',
          name: partner.name || ''
        };

        // Only update file if it wasn't just uploaded
        if (!this.fileJustUploaded) {
          patchData.file = partner.file || '';
        } 

        this.partnerForm.patchValue(patchData);
        
        // Set map center and marker
        if (partner.latitude && partner.longitude && parseFloat(partner.latitude) !== 0 && parseFloat(partner.longitude) !== 0) {
          const lat = parseFloat(partner.latitude);
          const lng = parseFloat(partner.longitude);
          this.mapCenter = { lat, lng };
          this.mapZoom = 15;
        } else {
          this.mapCenter = { lat: 51.935619, lng: 15.506186 };
          this.mapZoom = 12;
          this.partnerForm.patchValue({
            latitude: '51.935619',
            longitude: '15.506186'
          });
        }
        this.syncMapPointer();

        this.loading = false;
        
        this.loadBranches();
        this.loadOffers();
      },
      error: (err) => {
        console.error('Error loading partner:', err);
        this.error = 'Błąd podczas ładowania danych partnera';
        this.loading = false;
        this.snackBar.open('Nie udało się załadować danych partnera', 'Zamknij', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  loadBranches(): void {
    if (!this.partnerId) {
      this.branches = [];
      return;
    }
    
    this.branchesLoading = true;
    this.partnersService.getPartners({ parent_id: this.partnerId, order: 'asc', order_by: 'name' }).subscribe({
      next: (response) => {
        this.branches = response.items;
        this.branchesLoading = false;
      },
      error: (err) => {
        console.error('Error loading branches:', err);
        this.branches = [];
        this.branchesLoading = false;
        this.snackBar.open('Nie udało się załadować oddziałów', 'Zamknij', {
          duration: 3000,
          panelClass: ['warning-snackbar']
        });
      }
    });
  }

  editBranch(branch: Partner): void {
    this.partnerId = branch.id;
    this.branches = [];
    this.branchesLoading = false;
    
    this.snackBar.open(`Ładowanie danych oddziału: ${branch.name}`, 'Zamknij', {
      duration: 2000,
      panelClass: ['success-snackbar']
    });
    
    this.loadPartner();
  }

  goBackToMainPartner(): void {
    if (this.mainPartnerId) {
      this.partnerId = this.mainPartnerId;
      this.isEditingBranch = false;
      this.loadPartner();
      
      this.snackBar.open('Powrót do głównego partnera', 'Zamknij', {
        duration: 2000,
        panelClass: ['success-snackbar']
      });
    }
  }

  getPartnerLogoUrl(filename: string): string {
    return `${this.imagesUrl}/partners/${filename}`;
  }

  onLogoError(event: any): void {
    // Hide the image if it fails to load
    event.target.style.display = 'none';
  }

  onAddressInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addressQuery = input.value;
  }

  searchAddress(): void {
    if (!this.addressQuery) return;
    
    this.http.get<any>(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.addressQuery)}`
    ).subscribe(results => {
      if (results && results.length > 0) {
        const lat = parseFloat(results[0].lat);
        const lng = parseFloat(results[0].lon);
        this.mapCenter = { lat, lng };
        this.mapZoom = 19;
        
        // Update form fields
        this.partnerForm.patchValue({
          latitude: lat.toString(),
          longitude: lng.toString()
        });
        this.syncMapPointer();
      } else {
        this.snackBar.open('Nie znaleziono podanego adresu', 'Zamknij', {
          duration: 3000,
          panelClass: ['warning-snackbar']
        });
      }
    });
  }

  syncMapPointer(): void {
    const lat = parseFloat(this.partnerForm.get('latitude')?.value);
    const lng = parseFloat(this.partnerForm.get('longitude')?.value);
    this.mapPointer = (isFinite(lat) && isFinite(lng))
      ? { lat, lng, title: this.currentPartner?.name || 'Lokalizacja' }
      : null;
  }

  onMapLocationPicked(location: { lat: number; lng: number }): void {
    // Do NOT update mapCenter/mapZoom — that would trigger ngOnChanges → setView()
    // and reset any zoom the user applied manually. Just update the form and marker.
    this.partnerForm.patchValue({
      latitude: location.lat.toString(),
      longitude: location.lng.toString()
    });
    this.syncMapPointer();
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    
    // Create local preview immediately
    this.selectedLogoFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.localLogoPreview = e.target?.result as string;

    };
    reader.readAsDataURL(file);

    this.partnersService.uploadFile(file).subscribe({
      next: (res: any) => {
        if (res && res.file_name) {
          
          // Mark that file was just uploaded BEFORE updating form
          this.fileJustUploaded = true;
          
          // Use setValue to ensure proper form update
          this.partnerForm.get('file')?.setValue(res.file_name);
          // Also update the form directly to ensure change detection
          this.partnerForm.markAsDirty();
          this.partnerForm.updateValueAndValidity();
          
          // Trigger change detection
          this.cdr.detectChanges();
          
          
          // Keep local preview for now - don't clear it immediately
          // This way we can see the uploaded file in preview
          // this.localLogoPreview = undefined;
          // this.selectedLogoFile = undefined;
          
          this.snackBar.open('Logo zostało przesłane', 'Zamknij', {
            duration: 3000,
            panelClass: ['success-snackbar']
          });
        } 
      },
      error: (err) => {
        console.error('Upload error:', err);
        // Clear local preview on error
        this.localLogoPreview = undefined;
        this.selectedLogoFile = undefined;
        
        this.snackBar.open('Błąd podczas przesyłania logo', 'Zamknij', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  

  onSubmit(): void {
    if (this.partnerForm.invalid || !this.partnerId) {
      return;
    }

    this.saving = true;
    this.error = null;

    const formData = this.partnerForm.value;
    
    this.partnersService.updateCurrentPartner(this.partnerId, formData).pipe(takeUntil(this.destroy$)).subscribe({
      next: (updatedPartner) => {
        this.saving = false;
        this.fileJustUploaded = false; // Reset the flag after successful submit
        
        // Clear local preview after successful save
        this.localLogoPreview = undefined;
        this.selectedLogoFile = undefined;
        
        this.snackBar.open('Dane partnera zostały zaktualizowane', 'Zamknij', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
      },
      error: (err) => {
        console.error('Error updating partner:', err);
        this.error = 'Błąd podczas aktualizacji danych partnera';
        this.saving = false;
        this.fileJustUploaded = false; // Reset the flag on error too
        this.snackBar.open('Nie udało się zaktualizować danych partnera', 'Zamknij', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  // Offers Management Methods
  loadOffers(): void {
    if (!this.partnerId) return;
    
    this.offersLoading = true;
    this.offersService.getOffers({ 
      partner_id: this.partnerId 
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.offers = response.items || [];
        this.offersLoading = false;
      },
      error: (err) => {
        console.error('Error loading offers:', err);
        this.offers = [];
        this.offersLoading = false;
        this.snackBar.open('Nie udało się załadować ofert', 'Zamknij', {
          duration: 3000,
          panelClass: ['warning-snackbar']
        });
      }
    });
  }

  editOffer(offer: Offer): void {
    this.editingOfferId = offer.id;
    this.showOfferEdit = true;
  }

  createNewOffer(): void {
    this.editingOfferId = undefined;
    this.showOfferEdit = true;
  }

  closeOfferEdit(): void {
    this.showOfferEdit = false;
    this.editingOfferId = undefined;
    // Reload offers to reflect changes
    this.loadOffers();
  }

  deleteOffer(offer: Offer): void {
    if (!confirm(`Czy na pewno chcesz usunąć ofertę "${offer.title}"?`)) {
      return;
    }

    this.offersService.deleteOffer(offer.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Oferta została usunięta', 'Zamknij', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
        this.loadOffers(); // Reload the list
      },
      error: (err) => {
        console.error('Error deleting offer:', err);
        this.snackBar.open('Nie udało się usunąć oferty', 'Zamknij', {
          duration: 3000,
          panelClass: ['error-snackbar']
        });
      }
    });
  }

  formatValidDate(validDate: string): string {
    if (!validDate) return '-';
    
    try {
      // API format: "2013-12-23T09:39:43"
      const datePart = validDate.split('T')[0]; // "2013-12-23"
      const [year, month, day] = datePart.split('-');
      return `${day}.${month}.${year}`; // "23.12.2013"
    } catch (error) {
      return validDate; // Return original if parsing fails
    }
  }

}
